import type { Account, PlatformAccount, PublishSummary, Rule } from "@/Connect/types";
import { getIdToken } from "@/services/Auth";
import { getDefaultHeaders, request } from "@/utils/CreativeIntelligenceApi";

type AccountApiResponse = {
    accountsRules: [
        {
            rules: Rule[];
            platformAccount: PlatformAccount;
        }
    ];
    latestVersionId: string;
};

export async function getAccounts({ baseUrl, campaignId }: { baseUrl: string; campaignId: string }) {
    const url = `${baseUrl}/v1/publish-campaign/okta/${campaignId}/accounts/`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<void, AccountApiResponse>(url, headers);
}

export async function getCampaignPublishSummary({ baseUrl, campaignId }: { baseUrl: string; campaignId: string }) {
    const url = `${baseUrl}/v1/publish-rules/okta/campaigns/${campaignId}/status/`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<void, PublishSummary[]>(url, headers);
}

type AccountPublishDetailsApiResponse = {
    error: {
        message: string;
    };
};

export async function getAccountPublishDetails({
    baseUrl,
    campaignId,
    platformAccountId
}: {
    baseUrl: string;
    campaignId: string;
    platformAccountId: number;
}) {
    const url = `${baseUrl}/v1/publish-rules/okta/campaigns/${campaignId}/accounts/${platformAccountId}/updates/`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<void, AccountPublishDetailsApiResponse[]>(url, headers);
}

interface ValidateCampaingApiResponse {
    fileLocation: {
        bucket: string;
        key: string;
    };
}

export async function validateCampaign({
    baseUrl,
    campaignId,
    campaignData
}: {
    baseUrl: string;
    campaignId: string;
    campaignData: {
        latestVersionId: string;
        accountsRules: Account[];
        fileUrl: string;
    };
}) {
    const url = `${baseUrl}/v1/publish-campaign/okta/${campaignId}/validate/`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<typeof campaignData, ValidateCampaingApiResponse>(url, headers, "POST", campaignData);
}

export async function saveCampaign({
    baseUrl,
    campaignId,
    campaignData
}: {
    baseUrl: string;
    campaignId: string;
    campaignData: ValidateCampaingApiResponse & { latestVersionId: string };
}) {
    const url = `${baseUrl}/v1/publish-campaign/okta/${campaignId}/save/`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<typeof campaignData, { latestVersionId: string }>(url, headers, "POST", campaignData);
}

export async function getDesiredState({ baseUrl, campaignId }: { baseUrl: string; campaignId: string }) {
    const url = `${baseUrl}/v1/publish-rules/okta/build-desired-states`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<{ campaignId: string }, { bucket: string; key: string }[]>(url, headers, "POST", { campaignId });
}

export async function publishCampaign({ baseUrl, campaignId }: { baseUrl: string; campaignId: string }) {
    const url = `${baseUrl}/v1/publish-rules/okta/platforms/publish`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<
        { campaignId: string },
        { publishStatuses: { campaignId: string; status: "publishing"; account: PlatformAccount }[] }
    >(url, headers, "POST", { campaignId });
}

export async function getLocationSuggestions({ baseUrl, locationQuery }: { baseUrl: string; locationQuery: string }) {
    const url = `${baseUrl}/v1/openweather/okta/search-locations?key=${locationQuery}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<void, { name: string; country: string }[]>(url, headers);
}

export type InstagramAccountData = {
    id: string;
    username: string;
    name: string;
};

export async function getInstagramAccount({ baseUrl, campaignId }: { baseUrl: string; campaignId: string }) {
    const url = `${baseUrl}/v1/pubfb/instagram-user/${campaignId}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<InstagramAccountData>(url, headers);
}
